import React, { useCallback, useEffect, useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Inbox } from "lucide-react";
import { toast } from "sonner";

// Generic CRUD manager for simple master lists (categories, groups, price lists).
// fields: [{ key, label, type?: "text"|"number", placeholder? }]
export function MasterManager({ kind, title, fields, testid }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try { const r = await http.get(`/masters/${kind}`); setRows(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, `load ${title.toLowerCase()}s`)); }
  }, [kind, title]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({}); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...r }); setEditId(r.id); setOpen(true); };

  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Name is required");
    try {
      if (editId) await http.put(`/masters/${kind}/${editId}`, form);
      else await http.post(`/masters/${kind}`, form);
      toast.success(editId ? `${title} updated` : `${title} added`);
      setOpen(false); load();
    } catch (e) { toast.error(requestErrorMessage(e, editId ? `update ${title.toLowerCase()}` : `add ${title.toLowerCase()}`)); }
  };
  const del = async (id) => {
    if (!window.confirm(`Delete this ${title.toLowerCase()}?`)) return;
    try { await http.delete(`/masters/${kind}/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(requestErrorMessage(e, `delete ${title.toLowerCase()}`)); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={openNew} data-testid={`add-${testid}-button`}><Plus className="h-4 w-4 mr-1" /> New {title}</Button>
      </div>
      <Card className="p-0 overflow-hidden">
        {loadError && <div role="alert" className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            {fields.map((f) => <th key={f.key} className="px-4 py-3 font-medium">{f.label}</th>)}
            <th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`${testid}-row-${r.id}`}>
                {fields.map((f) => <td key={f.key} className="px-4 py-3">{f.type === "number" && r[f.key] != null ? r[f.key] : (r[f.key] || "—")}{f.suffix && r[f.key] != null && r[f.key] !== "" ? f.suffix : ""}</td>)}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`edit-${testid}-${r.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                    <button onClick={() => del(r.id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`delete-${testid}-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!loadError && rows.length === 0 && <tr><td colSpan={fields.length + 1} className="px-4 py-12 text-center text-muted-foreground"><Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />No {title.toLowerCase()} yet</td></tr>}
          </tbody>
        </table></div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? `Edit ${title}` : `New ${title}`}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            {fields.map((f) => (
              <div key={f.key}>
                <Label>{f.label}{f.key === "name" ? " *" : ""}</Label>
                <Input
                  type={f.type === "number" ? "number" : "text"}
                  value={form[f.key] ?? ""}
                  placeholder={f.placeholder || ""}
                  onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? +e.target.value : e.target.value })}
                  className="mt-1.5" data-testid={`${testid}-${f.key}-input`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid={`save-${testid}-button`}>Save {title}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
